import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { businessApi } from '../services/api';
import { Business } from '../types';
import { ImageUpload, GalleryUpload } from '../components/ImageUpload';
import { OpeningHours } from '../components/OpeningHours';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

const CATEGORIES = [
  'Gimnasio','CrossFit','Yoga','Pilates','Natación','Boxeo',
  'Artes Marciales','Danza','Bienestar','Fisioterapia',
  'Padel','Fútbol','Atletismo','Ciclismo','Escalada','Otra',
];

export function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'info'|'imagenes'|'horarios'>('info');
  const [form, setForm] = useState<any>({});
  const [gallery, setGallery] = useState<string[]>([]);

  useEffect(() => { if (id) fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const r = await businessApi.getBusiness(id!);
      const b = r.data.data.business;
      setBusiness(b);
      setGallery(Array.isArray(b.gallery) ? b.gallery : []);
      setForm({
        name: b.name || '', description: b.description || '',
        address: b.address || '', city: b.city || '',
        phone: b.phone || '', email: b.email || '',
        website: b.website || '', category: b.category || '',
        instagram: b.instagram || '', facebook: b.facebook || '',
        tiktok: b.tiktok || '', whatsapp: b.whatsapp || '',
        stripeKey: (b as any).stripeKey || '',
      });
    } catch { toast.error('Error cargando el negocio'); }
    finally { setIsLoading(false); }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API_URL}/businesses/${id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('✅ Negocio actualizado');
      setEditMode(false);
      fetchData();
    } catch { toast.error('Error al guardar'); }
    finally { setIsSaving(false); }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  if (!business) return (
    <div className="card p-8 text-center"><p className="text-secondary-500">Negocio no encontrado</p></div>
  );

  return (
    <div className="space-y-4">
      <style>{`
        .biz-tab { padding: 10px 18px; border: none; background: none; font-weight: 600; font-size: .85rem; color: #64748b; cursor: pointer; border-bottom: 2px solid transparent; transition: all .2s; font-family: inherit; white-space: nowrap; }
        .biz-tab.active { color: #3b82f6; border-bottom-color: #3b82f6; }
        .info-row { display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f1f5f9; }
        .info-icon { font-size: 1rem; flex-shrink: 0; margin-top: 2px; }
        .info-label { font-size: .72rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .06em; }
        .info-value { font-size: .88rem; color: #1e293b; font-weight: 500; margin-top: 2px; }
        .action-btn { display: flex; align-items: center; gap: 10px; padding: 14px 16px; background: white; border: 1px solid #e2e8f0; border-radius: 12px; text-decoration: none; color: #1e293b; font-weight: 600; font-size: .88rem; transition: all .2s; cursor: pointer; font-family: inherit; }
        .action-btn:hover { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
        .action-icon { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; flex-shrink: 0; }
      `}</style>

      {/* HEADER */}
      <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {/* Cover mini */}
        <div style={{ height: 80, background: business.coverImage ? `url(${business.coverImage}) center/cover` : 'linear-gradient(135deg, #1e293b, #334155)', position: 'relative' }}>
          <a href={`https://admin.fitpulse.es/b/${business.slug}`} target="_blank" rel="noreferrer"
            style={{ position: 'absolute', top: 10, right: 12, background: 'rgba(255,255,255,.9)', color: '#1e293b', fontSize: '.72rem', fontWeight: 700, padding: '5px 12px', borderRadius: 100, textDecoration: 'none' }}>
            🌐 Ver pública
          </a>
        </div>
        <div style={{ padding: '0 16px 16px', display: 'flex', alignItems: 'flex-end', gap: 14, marginTop: -24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: business.logo ? `url(${business.logo}) center/cover` : 'linear-gradient(135deg, #3b82f6, #8b5cf6)', border: '3px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: '1.3rem', flexShrink: 0, boxShadow: '0 4px 12px rgba(0,0,0,.15)', overflow: 'hidden' }}>
            {!business.logo && business.name.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, paddingBottom: 4 }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>{business.name}</div>
            <div style={{ fontSize: '.75rem', color: '#64748b' }}>
              {(business as any).city && `📍 ${(business as any).city}`}
              {(business as any).category && ` · ${(business as any).category}`}
            </div>
          </div>
          <button onClick={() => { setEditMode(!editMode); setActiveTab('info'); }}
            style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: editMode ? '#fee2e2' : '#eff6ff', color: editMode ? '#dc2626' : '#1d4ed8', fontWeight: 700, fontSize: '.78rem', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
            {editMode ? '✕ Cancelar' : '✏️ Editar'}
          </button>
        </div>

        {/* TABS */}
        <div style={{ borderTop: '1px solid #f1f5f9', display: 'flex', overflowX: 'auto' }}>
          <button className={`biz-tab ${activeTab==='info'?'active':''}`} onClick={() => setActiveTab('info')}>📋 Información</button>
          <button className={`biz-tab ${activeTab==='imagenes'?'active':''}`} onClick={() => setActiveTab('imagenes')}>🖼️ Imágenes</button>
          <button className={`biz-tab ${activeTab==='horarios'?'active':''}`} onClick={() => setActiveTab('horarios')}>🕐 Horarios</button>
        </div>
      </div>

      {/* TAB: INFO */}
      {activeTab === 'info' && (
        <div className="card p-5">
          {editMode ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="label">Nombre *</label>
                  <input className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nombre del negocio"/>
                </div>
                <div>
                  <label className="label">Categoría</label>
                  <select className="input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                    <option value="">— Seleccionar —</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Descripción</label>
                  <textarea className="input" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe tu negocio..."/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Ciudad</label>
                    <input className="input" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Madrid"/>
                  </div>
                  <div>
                    <label className="label">Teléfono</label>
                    <input className="input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+34 600..."/>
                  </div>
                </div>
                <div>
                  <label className="label">Dirección</label>
                  <input className="input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Calle..."/>
                </div>
                <div>
                  <label className="label">Email</label>
                  <input className="input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="info@..."/>
                </div>
                <div>
                  <label className="label">Web</label>
                  <input className="input" value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://..."/>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                <p className="label mb-3">Redes sociales</p>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="label">📸 Instagram</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 border border-r-0 border-secondary-300 bg-secondary-50 text-secondary-500 text-sm rounded-l-lg">@</span>
                      <input className="input rounded-l-none" value={form.instagram} onChange={e => setForm({...form, instagram: e.target.value})} placeholder="tunegocio"/>
                    </div>
                  </div>
                  <div>
                    <label className="label">🎵 TikTok</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 border border-r-0 border-secondary-300 bg-secondary-50 text-secondary-500 text-sm rounded-l-lg">@</span>
                      <input className="input rounded-l-none" value={form.tiktok} onChange={e => setForm({...form, tiktok: e.target.value})} placeholder="tunegocio"/>
                    </div>
                  </div>
                  <div>
                    <label className="label">💬 WhatsApp</label>
                    <input className="input" value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} placeholder="34600000000"/>
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                <p className="label mb-2">💳 Pagos online (Stripe)</p>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: '.78rem', color: '#1d4ed8' }}>
                  Añade tu clave secreta de Stripe para aceptar pagos en eventos de pago. Ve a dashboard.stripe.com → Developers → API Keys → Secret key.
                </div>
                <input className="input" type="password" value={form.stripeKey || ''} onChange={e => setForm({...form, stripeKey: e.target.value})} placeholder="sk_live_... o sk_test_..."/>
                {form.stripeKey && <p style={{ fontSize: '.72rem', color: '#15803d', marginTop: 4 }}>✅ Clave configurada</p>}
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setEditMode(false)} className="btn-secondary flex-1">Cancelar</button>
                <button onClick={handleSave} disabled={isSaving} className="btn-primary flex-1">
                  {isSaving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              {business.description && (
                <p style={{ fontSize: '.88rem', color: '#475569', lineHeight: 1.6, marginBottom: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 10 }}>
                  {business.description}
                </p>
              )}
              {[
                { icon: '🏙️', label: 'Ciudad', value: (business as any).city },
                { icon: '📍', label: 'Dirección', value: business.address },
                { icon: '📞', label: 'Teléfono', value: business.phone },
                { icon: '✉️', label: 'Email', value: business.email },
                { icon: '🌐', label: 'Web', value: business.website },
                { icon: '📸', label: 'Instagram', value: (business as any).instagram ? `@${(business as any).instagram}` : null },
                { icon: '🎵', label: 'TikTok', value: (business as any).tiktok ? `@${(business as any).tiktok}` : null },
                { icon: '💬', label: 'WhatsApp', value: (business as any).whatsapp },
              ].filter(r => r.value).map((r, i) => (
                <div key={i} className="info-row">
                  <span className="info-icon">{r.icon}</span>
                  <div><div className="info-label">{r.label}</div><div className="info-value">{r.value}</div></div>
                </div>
              ))}
              {!business.address && !business.phone && !business.email && (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: '.85rem' }}>
                  <p>Sin información. Pulsa Editar para completar el perfil.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB: IMÁGENES */}
      {activeTab === 'imagenes' && (
        <div className="card p-5 space-y-6">
          <div>
            <p className="label mb-3">📸 Imagen de portada</p>
            <ImageUpload type="cover" entityId={id!} currentImage={business.coverImage}
              label="Subir portada" aspectRatio="16/5"
              onSuccess={url => setBusiness(prev => prev ? { ...prev, coverImage: url } : prev)} />
          </div>
          <div>
            <p className="label mb-3">🏷️ Logo del negocio</p>
            <ImageUpload type="logo" entityId={id!} currentImage={business.logo}
              aspectRatio="1/1"
              onSuccess={url => setBusiness(prev => prev ? { ...prev, logo: url } : prev)} />
          </div>
          <div>
            <p className="label mb-3">🖼️ Galería de fotos</p>
            <GalleryUpload businessId={id!} gallery={gallery} onUpdate={setGallery} />
          </div>
        </div>
      )}

      {/* TAB: HORARIOS */}
      {activeTab === 'horarios' && (
        <OpeningHours businessId={id!} initialHours={(business as any).openingHours || undefined} />
      )}

      {/* ACCIONES RÁPIDAS */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-secondary-500 uppercase tracking-wide mb-3">Gestión rápida</h3>
        <div className="grid grid-cols-1 gap-2">
          <a href={`/events?businessId=${business.id}`} className="action-btn">
            <div className="action-icon" style={{ background: '#eff6ff' }}>📅</div>
            <div><div style={{ fontWeight: 700 }}>Eventos</div><div style={{ fontSize: '.75rem', color: '#64748b' }}>Publicar y gestionar eventos</div></div>
            <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>→</span>
          </a>
          <a href={`/bookings?businessId=${business.id}`} className="action-btn">
            <div className="action-icon" style={{ background: '#f0fdf4' }}>📋</div>
            <div><div style={{ fontWeight: 700 }}>Reservas</div><div style={{ fontSize: '.75rem', color: '#64748b' }}>Ver inscripciones de clientes</div></div>
            <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>→</span>
          </a>
          <a href={`https://admin.fitpulse.es/b/${business.slug}`} target="_blank" rel="noreferrer" className="action-btn">
            <div className="action-icon" style={{ background: '#fef3c7' }}>🌐</div>
            <div><div style={{ fontWeight: 700 }}>Página pública</div><div style={{ fontSize: '.75rem', color: '#64748b' }}>Ver cómo te ven tus clientes</div></div>
            <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>→</span>
          </a>
        </div>
      </div>
    </div>
  );
}
